import {Box} from 'platform/foundation';

import {useState} from 'react';

import {mergeAll} from 'ramda';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, refreshDmsDatagrid, Section, SectionTab, TireSetDetail} from '@dms/shared';

import type {QueryFilterObject} from 'features/eag-grid';

import {tireInventorySearchParams} from '../../../../utils/tireInventorySearchParams';
import {useTiresInventoryUrl} from '../../hooks/useTiresInventoryUrl';
import {
  EditTireSetData,
  TIRE_ORDER_SET_PREDEFINED_COLUMNS,
  useTireOrderSetActions,
} from './hooks/useTireOrderSetActions';

export function SetOfTires() {
  const {orderId} = useTiresInventoryUrl();
  const [editingTireSetData, setEditingTireSetData] = useState<EditTireSetData | null>(null);

  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {orderId}]);

  const handleEditTireSet = (data: EditTireSetData) => {
    setEditingTireSetData(data);
  };

  const orderSetActionCallback = useTireOrderSetActions(orderId, handleEditTireSet);

  const tabs: SectionTab[] = [
    {
      content: (
        //  Datagrid inside tabs need to be wrapped in Box with unique key, to force Datagrid rerender between tab switching
        <Box key="tire-order-set">
          <DmsDataGrid
            gridCode="tire-order-set"
            actionCallback={orderSetActionCallback}
            predefinedColumns={TIRE_ORDER_SET_PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={testIds.tiresInventory.tireOrderDetail('setOfTires')}
            autoHeight
          />
        </Box>
      ),
      queryId: tireInventorySearchParams.tireOrderDetial.setOfTireTabs.tires,
      title: i18n.t('page.tiresInventory.labels.tires'),
    },
    {
      content: (
        //  Datagrid inside tabs need to be wrapped in Box with unique key, to force Datagrid rerender between tab switching
        <Box key="tire-order-movement">
          <DmsDataGrid
            gridCode="tire-order-movement"
            actionCallback={orderSetActionCallback}
            predefinedColumns={TIRE_ORDER_SET_PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={testIds.tiresInventory.tireOrderDetail('tiresMovement')}
            autoHeight
          />
        </Box>
      ),
      queryId: tireInventorySearchParams.tireOrderDetial.setOfTireTabs.tiresMovement,
      title: i18n.t('page.tiresInventory.labels.tiresMovement'),
    },
    {
      content: (
        <Box key="tire-order-set-archived">
          <DmsDataGrid
            gridCode="tire-order-set-archived"
            actionCallback={orderSetActionCallback}
            predefinedColumns={TIRE_ORDER_SET_PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={testIds.tiresInventory.tireOrderDetail('tiresArchived')}
            autoHeight
          />
        </Box>
      ),
      queryId: tireInventorySearchParams.tireOrderDetial.setOfTireTabs.archived,
      title: i18n.t('page.tiresInventory.labels.archived'),
    },
  ];

  const handleCloseEditTireSet = () => {
    setEditingTireSetData(null);
  };

  const handleEditTireSetSave = () => {
    setEditingTireSetData(null);
    refreshDmsDatagrid('tire-order-set');
    refreshDmsDatagrid('tire-order-movement');
  };

  return (
    <>
      <Section data-testid={testIds.tiresInventory.tireOrderDetail('overview')} tabs={tabs} />
      {editingTireSetData && (
        <TireSetDetail
          tireOrderId={editingTireSetData.tireOrderId}
          tireSetId={editingTireSetData.setId}
          onClose={handleCloseEditTireSet}
          onEdit={handleEditTireSetSave}
        />
      )}
    </>
  );
}
